import React, { useCallback, useRef } from "react";
import {
  ReactFlow,
  Controls,
  Background,
  MiniMap,
  addEdge,
  useNodesState,
  useEdgesState,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { FaTrash } from "react-icons/fa";

import TriggerNode from "./nodes/TriggerNode";
import SendMessageNode from "./nodes/SendMessageNode";
import DelayNode from "./nodes/DelayNode";
import JitterNode from "./nodes/JitterNode";
import ConditionNode from "./nodes/ConditionNode";
import SendToAINode from "./nodes/SendToAINode";
import TriggerWebhookNode from "./nodes/TriggerWebhookNode";
import LoopNode from './nodes/LoopNode';
import WaitForReplyNode from './nodes/WaitForReplyNode';
import AIConversationNode from './nodes/AIConversationNode';
import AIAgentNode from './nodes/AIAgentNode';
import SendVoiceNoteNode from './nodes/SendVoiceNoteNode';
import HttpRequestNode from './nodes/HttpRequestNode';
import ShopifyTriggerNode from './nodes/ShopifyTriggerNode';
import SendToGroupNode from './nodes/SendToGroupNode';
import AIAnalyzerNode from './nodes/AIAnalyzerNode';
import NodePalette from "./NodePalette";
import NodeConfigPanel from "./NodeConfigPanel";

const nodeTypes = {
  trigger: TriggerNode,
  shopifyTrigger: ShopifyTriggerNode,
  sendMessage: SendMessageNode,
  delay: DelayNode,
  jitter: JitterNode,
  condition: ConditionNode,
  sendToAI: SendToAINode,
  triggerWebhook: TriggerWebhookNode,
  loop: LoopNode,
  waitForReply: WaitForReplyNode,
  aiConversation: AIConversationNode,
  aiAgent: AIAgentNode,
  sendVoiceNote: SendVoiceNoteNode,
  httpRequest: HttpRequestNode,
  sendToGroup: SendToGroupNode,
  aiAnalyzer: AIAnalyzerNode,
};

const defaultNodeData = {
  trigger: { matchType: "contains", keyword: "" },
  shopifyTrigger: { integrationId: "", integrationName: "", topic: "orders/create" },
  sendMessage: { message: "", mediaUrl: "" },
  delay: { duration: 5, unit: "minutes" },
  jitter: { minDuration: 10, maxDuration: 60, unit: "seconds" },
  condition: { matchType: "contains", keyword: "" },
  sendToAI: { prompt: "" },
  triggerWebhook: { webhookUrl: "", customHeaders: "" },
  loop: { message: "", mediaUrl: "", recipients: [], repeatCount: 0, intervalType: "simple", intervalValue: 24, intervalUnit: "hours", cronExpression: "" },
  waitForReply: { timeoutValue: 24, timeoutUnit: "hours" },
  aiConversation: { prompt: "", knowledgeBaseIds: [], exitKeyword: "stop", maxTurns: 0 },
  aiAgent: {
    prompt: "You are a helpful sales and support assistant for our business. Be warm, concise and accurate.",
    objective: "",
    knowledgeBaseIds: [],
    replyToFirstMessage: true,
    pauseOnHumanReply: true,
    handoffKeywords: ["human", "agent", "representative", "talk to someone"],
    handoffMessage: "Thanks for your patience — I'm connecting you to a member of our team who can help with this.",
    exitKeyword: "stop",
    maxTurns: 0,
  },
  sendVoiceNote: { message: "", ttsProvider: "elevenlabs", voiceId: "", voice: "alloy" },
  httpRequest: { method: "GET", url: "", headers: "", body: "" },
  sendToGroup: { message: "", mediaUrl: "", groupIds: [], groupNames: [] },
  aiAnalyzer: { instruction: "", analysisKinds: ["any"], respondInChat: true, knowledgeBaseIds: [] },
};

let nodeIdCounter = 0;

export default function FlowCanvas({
  initialNodes,
  initialEdges,
  onSave,
  saving,
  sessions = [],
  selectedSessionId = "",
}) {
  const reactFlowWrapper = useRef(null);
  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes || []);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges || []);
  const [selectedNode, setSelectedNode] = React.useState(null);
  const reactFlowInstance = useRef(null);

  // Set counter based on existing nodes
  React.useEffect(() => {
    const maxId = (initialNodes || []).reduce((max, n) => {
      const num = parseInt(n.id.replace("node-", ""), 10);
      return isNaN(num) ? max : Math.max(max, num);
    }, 0);
    nodeIdCounter = maxId + 1;
  }, [initialNodes]);

  const onConnect = useCallback(
    (params) => {
      setEdges((eds) =>
        addEdge(
          {
            ...params,
            id: `edge-${Date.now()}`,
            animated: true,
            style: { stroke: "#00C897", strokeWidth: 2 },
          },
          eds
        )
      );
    },
    [setEdges]
  );

  const onNodeClick = useCallback((_, node) => {
    setSelectedNode(node);
  }, []);

  const onPaneClick = useCallback(() => {
    setSelectedNode(null);
  }, []);

  // Drag and drop from palette
  const onDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
  }, []);

  const onDrop = useCallback(
    (event) => {
      event.preventDefault();
      const type = event.dataTransfer.getData("application/reactflow");
      if (!type) return;

      const position = reactFlowInstance.current?.screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      });

      if (!position) return;

      const newNode = {
        id: `node-${nodeIdCounter++}`,
        type,
        position,
        data: { ...defaultNodeData[type] },
      };

      setNodes((nds) => [...nds, newNode]);
    },
    [setNodes]
  );

  // Update node data from config panel
  const handleNodeChange = useCallback(
    (updatedNode) => {
      setNodes((nds) =>
        nds.map((n) =>
          n.id === updatedNode.id ? { ...n, data: updatedNode.data } : n
        )
      );
      setSelectedNode(updatedNode);
    },
    [setNodes]
  );

  // Delete node
  const handleDeleteNode = useCallback(
    (nodeId) => {
      setNodes((nds) => nds.filter((n) => n.id !== nodeId));
      setEdges((eds) =>
        eds.filter((e) => e.source !== nodeId && e.target !== nodeId)
      );
      setSelectedNode(null);
    },
    [setNodes, setEdges]
  );

  // Keyboard Delete/Backspace handler
  const handleKeyDown = useCallback((e) => {
    if ((e.key === 'Delete' || e.key === 'Backspace') && selectedNode) {
      e.preventDefault();
      handleDeleteNode(selectedNode.id);
    }
  }, [selectedNode, handleDeleteNode]);

  // Mobile-friendly tap-to-add from palette
  const handleAddNodeFromPalette = useCallback((type) => {
    const position = {
      x: 250 + Math.random() * 100,
      y: 50 + nodes.length * 120,
    };
    const newNode = {
      id: `node-${Date.now()}`,
      type,
      position,
      data: { ...(defaultNodeData[type] || {}) },
    };
    setNodes((nds) => [...nds, newNode]);
  }, [nodes.length, setNodes]);

  const handleSave = () => {
    onSave(nodes, edges);
  };

  return (
    <div className="flex flex-col h-full">
      {/* Toolbar */}
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-surface-border bg-surface-card">
        <NodePalette onAddNode={handleAddNodeFromPalette} />
        <button
          className="btn-primary btn-sm flex items-center gap-1.5"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? "Saving..." : "Save Flow"}
        </button>
      </div>

      {/* Canvas + Config Panel */}
      <div className="flex flex-1 overflow-hidden">
        <div
          className="flex-1 relative outline-none"
          ref={reactFlowWrapper}
          tabIndex={0}
          onKeyDown={handleKeyDown}
        >
          {/* Floating action toolbar for selected nodes */}
          {selectedNode && (
            <div className="absolute top-3 left-1/2 -translate-x-1/2 z-10 flex items-center gap-2 bg-surface-card border border-surface-border rounded-lg shadow-lg px-3 py-2">
              <span className="text-xs text-ink-muted font-medium">
                {selectedNode.type === 'trigger' ? 'Trigger' : selectedNode.type === 'shopifyTrigger' ? 'Shopify Event' : selectedNode.type === 'sendMessage' ? 'Send Message' : selectedNode.type === 'delay' ? 'Delay' : selectedNode.type === 'jitter' ? 'Jitter' : selectedNode.type === 'condition' ? 'Condition' : selectedNode.type === 'loop' ? 'Loop' : selectedNode.type === 'triggerWebhook' ? 'Webhook' : selectedNode.type === 'sendToAI' ? 'AI' : selectedNode.type === 'waitForReply' ? 'Wait for Reply' : selectedNode.type === 'aiConversation' ? 'AI Conversation' : selectedNode.type === 'aiAgent' ? 'AI Agent' : selectedNode.type === 'sendVoiceNote' ? 'Voice Note' : selectedNode.type === 'httpRequest' ? 'HTTP Request' : selectedNode.type === 'sendToGroup' ? 'Group Follow-Up' : selectedNode.type === 'aiAnalyzer' ? 'AI Analyzer' : selectedNode.type}
              </span>
              <button
                onClick={() => handleDeleteNode(selectedNode.id)}
                className="flex items-center gap-1 px-2 py-1 rounded text-xs font-medium text-red-600 bg-red-50 hover:bg-red-100 transition-colors"
              >
                <FaTrash size={10} /> Delete
              </button>
            </div>
          )}

          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onNodeClick={onNodeClick}
            onPaneClick={onPaneClick}
            onDragOver={onDragOver}
            onDrop={onDrop}
            onInit={(instance) => {
              reactFlowInstance.current = instance;
            }}
            nodeTypes={nodeTypes}
            fitView
            defaultEdgeOptions={{
              animated: true,
              style: { stroke: "#00C897", strokeWidth: 2 },
            }}
            proOptions={{ hideAttribution: true }}
          >
            <Background
              variant="dots"
              gap={20}
              size={1}
              color="#d1d5db"
            />
            <Controls
              position="bottom-left"
              className="!bg-surface-card !border-surface-border !shadow-sm !rounded-lg"
            />
            <MiniMap
              position="bottom-right"
              nodeColor={(n) => {
                const colors = {
                  trigger: "#00C897",
                  shopifyTrigger: "#059669",
                  sendMessage: "#3B82F6",
                  delay: "#F59E0B",
                  jitter: "#D946EF",
                  condition: "#8B5CF6",
                  sendToAI: "#6366F1",
                  triggerWebhook: "#EF4444",
                  loop: "#14B8A6",
                  waitForReply: "#F97316",
                  aiConversation: "#7C3AED",
                  aiAgent: "#C026D3",
                  sendVoiceNote: "#EC4899",
                  httpRequest: "#06B6D4",
                  sendToGroup: "#059669",
                  aiAnalyzer: "#0284C7",
                };
                return colors[n.type] || "#6B7280";
              }}
              className="!bg-surface-card !border-surface-border !rounded-lg"
            />
          </ReactFlow>
        </div>

        {/* Config panel - responsive */}
        {selectedNode && (
          <>
            {/* Mobile: overlay */}
            <div className="md:hidden fixed inset-0 z-50 bg-black/30" onClick={() => setSelectedNode(null)} />
            <div className="md:hidden fixed inset-x-0 bottom-0 z-50 max-h-[70vh] bg-surface-card rounded-t-2xl shadow-2xl overflow-y-auto">
              <NodeConfigPanel node={selectedNode} onChange={handleNodeChange} onDelete={handleDeleteNode} onClose={() => setSelectedNode(null)} nodes={nodes} edges={edges} sessions={sessions} selectedSessionId={selectedSessionId} />
            </div>
            {/* Desktop: sidebar */}
            <div className="hidden md:block">
              <NodeConfigPanel node={selectedNode} onChange={handleNodeChange} onDelete={handleDeleteNode} onClose={() => setSelectedNode(null)} nodes={nodes} edges={edges} sessions={sessions} selectedSessionId={selectedSessionId} />
            </div>
          </>
        )}
      </div>
    </div>
  );
}
